import iconNewWindow from "assets/IconNewWindow.svg?url";
import iconWindowsDevice from "assets/IconWindowsDevice.svg?url";

import NuSingleSelect from "js/components/NuSingleSelect";
import Checkbox from "js/components/shared/Checkbox";
import RuleStatusToggle from "js/components/shared/RuleStatusToggle";
import StandardModal from "js/components/shared/StandardModal";
import DisableKeyboardNavigation from "js/components/utils/DisableKeyboardNavigation";
import useStore from "stores/index";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { getUserDisplayTextForSelect, userDisplayText } from "js/utils/userUtils";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import SafeImage from "../SafeImage";
import { DangerButton } from "../shared/button/DangerButton";
import { GreyedButton } from "../shared/button/GreyedButton";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import { RuleStatusPill } from "../shared/RuleStatusPill";
import { useClassroomPolicyStore } from "stores/v2/classroomPolicyStore";

const SIGNATURE = "SIGNATURE";
const APP_CONTROL = "APP_CONTROL";

export default class CreatePolicy extends React.Component {
	constructor(props) {
		super(props);
		const store = useStore.getState();
		this.state = {
			id: undefined,
			signature: undefined,
			user: undefined,
			originalUser: undefined,
			editedlUser: undefined,
			type: "blocked",
			originalType: "blocked",
			signatureValueInvalid: false,
			signatureError: "",
			userValueInvalid: false,
			userError: "",
			website: undefined,
			wholeClass: true,
			modifiedWebsite: false,
			classwizeConfig: store.classwizeConfig,
			saving: false,
			duplicateOrConflictRule: undefined,
			validatedPolicy: undefined,
			resolving: false,
			validationErrorCode: undefined,
			signatureType: null,
			isBrowser: false,
			appControlValidationError: false,
			networkError: false,
		};

		this.formRef = React.createRef();
		this.featureFlags = useStore.getState().featureToggles;
		this.unsubClasswizeConfigStore = null;
	}

	componentDidMount() {
		this.unsubClasswizeConfigStore = useStore.subscribe(
			(state) => [state.classwizeConfig],

			() => this.setState({ classwizeConfig: useStore.getState().classwizeConfig })
		);
	}

	componentWillUnmount() {
		this.unsubClasswizeConfigStore();
	}

	componentDidUpdate(prevProps) {
		if (_.isEmpty(this.props.nativeAppControl) && !_.isEqual(this.props.policy, prevProps.policy)) {
			this.setState({
				signatureType: SIGNATURE,
				id: this.props.policy.id,
				signature: this.props.policy.tag,
				website: this.props.policy.website,
				user: this.props.policy.user,
				originalUser: this.props.policy.user,
				type: this.props.policy.type === undefined ? "blocked" : this.props.policy.type,
				originalType: this.props.policy.type === undefined ? "blocked" : this.props.policy.type,
				wholeClass: !this.props.policy.user,
				modifiedWebsite: false,
			});

			if (
				!_.isEqual(this.props.validationResponseData, prevProps.validationResponseData) &&
				this.props.validationResponseData.networkError
			) {
				this.handleOnApiError();
			} else if (!_.isEqual(this.props.validationResponseData, prevProps.validationResponseData)) {
				this.handleValidationResponse(this.props.validationResponseData, this.props.policy);
			}
		} else if (!_.isEmpty(this.props.nativeAppControl) && !_.isEqual(this.props.nativeAppControl, prevProps.nativeAppControl)) {
			const user = useSelectedClassroomStore
				.getState()
				.students?.find((student) => student.stableIdV1 === this.props.nativeAppControl.studentStableIdV1)?.username;
			this.setState({
				signatureType: APP_CONTROL,
				signature: this.props.nativeAppControl.appControlId,
				user: user,
				originalUser: user,
				wholeClass: _.isEmpty(this.props.nativeAppControl.studentStableIdV1),
				type: this.props.nativeAppControl.action === "block" ? "blocked" : "allowed",
			});
		}
	}

	prepareSelectorItems = () => {
		const ret = [];

		const availableSignatures = this.props.signatureDb.signatures;
		availableSignatures.sort(function (a, b) {
			return a.name.localeCompare(b.name);
		});

		const availableNativeAppControls = this.props.nativeAppControlsDb.nativeAppControls;
		availableNativeAppControls.sort(function (a, b) {
			return a.name.localeCompare(b.name);
		});

		ret.push({ type: "section", text: "signatures and categories" });
		for (const signature of availableSignatures) {
			if (
				signature.enabled &&
				signature.id !== "sphirewall.application.porn" &&
				signature.id !== "sphirewall.application.childporn" &&
				signature.id !== "sphirewall.application.offensive"
			) {
				ret.push({ value: signature.id, text: signature.name });
			}
		}

		if (this.featureFlags.isNativeAppControlsEnabled()) {
			ret.push({ type: "section", text: "app controls" });
			for (const appControl of availableNativeAppControls) {
				let icons = [];
				if (appControl.platforms.includes("windows")) {
					icons.push(iconWindowsDevice);
				}
				ret.push({ value: appControl.id, text: appControl.name, icons });
			}
		}

		ret.push({
			shouldShow: function (value) {
				return this.isOk(value);
			},
			renderText: function (value) {
				return "Website " + value;
			},
			isOk: function (value) {
				return this.transformFilter(value) !== "";
			},
			transformFilter: function (value) {
				const re = /^(http:\/\/|https:\/\/|)([a-z0-9A-Z.-]+\.?)\.([a-z0-9A-Z.-]+)(\/|)(.*)/i;
				let url = "";
				if (re.test(value)) {
					const m = re.exec(value);
					if (m[2] !== "") {
						url += m[2];
					}
					if (m[3] !== "") {
						url += "." + m[3];
					}
					if (m[4] !== "") {
						url += m[4];
					}
					if (m[5] !== "") {
						url += m[5];
					}
				}
				return url;
			},
			dynamic: true,
			value: "website",
		});

		// need to add this, otherwise when editing a rule set on a website, nothing shows in NuSingleSelect
		if (this.props.policy.website) {
			ret.push({
				value: this.props.policy.website,
				text: this.props.policy.website,
			});
		}

		return ret;
	};

	prepareAvailableUsers = () => {
		const ret = [];
		this.props.users.sort(function (a, b) {
			return a.username.localeCompare(b.username);
		});

		for (const user of this.props.users) {
			ret.push({
				text: getUserDisplayTextForSelect(user),
				value: user.username,
			});
		}

		return ret;
	};

	changeSignature = (value, filter) => {
		if (value === "website") {
			this.setState({
				signatureType: SIGNATURE,
				signature: undefined,
				website: filter,
				signatureValueInvalid: false,
				signatureError: "",
				modifiedWebsite: true,
			});
			return;
		} else if (value === this.props.policy.website) {
			// catches edge case where the user changes an existing
			// website rule, and selects the same website, causes website to
			// be set as the signature.
			this.setState({
				signatureType: SIGNATURE,
				signature: undefined,
				website: value,
				signatureValueInvalid: false,
				signatureError: "",
				modifiedWebsite: false,
			});
			return;
		}

		const signature = this.props.signatureDb.find(value);
		if (signature) {
			this.setState({
				signatureType: SIGNATURE,
				signature: value,
				website: undefined,
				signatureValueInvalid: false,
				signatureError: "",
				modifiedWebsite: true,
				isBrowser: signature.isBrowser,
			});
		} else {
			const appControl = this.props.nativeAppControlsDb.find(value);
			// it's a native app control, not a regular signature
			this.setState({
				signatureType: APP_CONTROL,
				signature: value,
				isBrowser: appControl?.isBrowser,
			});
		}
	};

	changeUser = (value) => {
		this.setState({
			user: value,
			userValueInvalid: false,
			userError: "",
		});
	};

	changeType = (value) => {
		this.setState({
			type: value,
			actionValueInvalid: value === undefined,
		});
	};

	changeWholeClass = (value) => {
		this.setState({
			wholeClass: value,
			user: undefined,
		});
	};

	validate = () => {
		let succeeded = true;
		if (!_.isEmpty(this.props.nativeAppControl)) {
			return succeeded;
		}

		if (this.state.signature === undefined && this.state.website === undefined) {
			succeeded = false;
			this.setState({
				signatureValueInvalid: true,
				signatureError: "Please select something to control",
			});
		} else {
			this.setState({
				signatureValueInvalid: false,
				signatureError: "",
			});
		}

		if (!this.state.wholeClass && (this.state.user === undefined || this.state.user === "")) {
			succeeded = false;
			this.setState({
				userValueInvalid: true,
				userError: "Please select a student or class",
			});
		} else {
			this.setState({
				userValueInvalid: false,
				userError: "",
			});
		}

		return succeeded;
	};

	getRuleFromClassroomPolicyStore = (ruleId) => {
		const cpsState = useClassroomPolicyStore.getState();
		let filteredRule = cpsState.blocked.filter((rule) => rule.id === ruleId)[0];
		if (filteredRule) {
			const filteredRuleCopy = JSON.parse(JSON.stringify(filteredRule));
			filteredRuleCopy["type"] = "blocked";
			return filteredRuleCopy;
		}
		filteredRule = cpsState.allowed.filter((rule) => rule.id === ruleId)[0];
		if (filteredRule) {
			const filteredRuleCopy = JSON.parse(JSON.stringify(filteredRule));
			filteredRuleCopy["type"] = "allowed";
			return filteredRuleCopy;
		}
		return {};
	};

	handleValidationResponse = (data = {}, originalRule) => {
		if (data.error) {
			const newState = {
				saving: false,
				validationErrorCode: data.error[0].code,
				validatedPolicy: {
					tag: data.error[0].tag,
					website: data.error[0].website,
				},
			};

			//for CW007, we don't need to revert the changes
			if (this.props.editing && !["CW007"].includes(newState.validationErrorCode)) {
				if (!originalRule) {
					originalRule = this.getRuleFromClassroomPolicyStore(this.state.id);
				}

				newState["type"] = originalRule.type;
				newState["originalType"] = originalRule.type ? originalRule.type : "blocked";
				newState["wholeClass"] = originalRule.user ? false : true;
				newState["editedlUser"] = this.state.user;
				newState["user"] = originalRule.user;
			}

			if (newState.validationErrorCode.includes("CW")) {
				newState["duplicateOrConflictRule"] = this.getRuleFromClassroomPolicyStore(data.error[0].id[0]);
			} else if (newState.validationErrorCode.includes("SM")) {
				newState["duplicateOrConflictRule"] = {
					tag: data.error[0].id[0],
				};
			} else {
				console.log("Unhandled type of validation error");
			}

			this.setState(newState);
		} else {
			this.close();
		}
	};

	handleAppControlsValidationResponse = (resp) => {
		if (resp.error) {
			this.setState({ appControlValidationError: true, saving: false });
		} else {
			this.close();
		}
	};

	handleOnApiError = () => {
		const newState = { saving: false, networkError: true };
		this.setState(newState);
	};

	submit = (event = {}) => {
		if (!this.validate()) return;
		this.setState({ saving: true });

		if (this.state.signatureType === SIGNATURE) {
			if (this.state.type === "blocked") {
				useClassroomPolicyStore.getState().addBlockedRule(
					{
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: true,
						user: this.state.wholeClass ? undefined : this.state.user,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			} else {
				useClassroomPolicyStore.getState().addAllowedRule(
					{
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: true,
						user: this.state.wholeClass ? undefined : this.state.user,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			}
		} else {
			// native app control signature type
			const appControl = this.props.nativeAppControlsDb.find(this.state.signature);
			useClassroomPolicyStore.getState().addAppControl(
				{
					classroomStableIdV1: useSelectedClassroomStore.getState().stableIdV1,
					classroomId: useSelectedClassroomStore.getState().id,
					classroomSourceName: useSelectedClassroomStore.getState().sourceId,
					data: {
						name: appControl.name,
						isClassroomRule: this.state.wholeClass,
						studentStableIdV1: this.state.wholeClass
							? null
							: useSelectedClassroomStore.getState().students?.find((student) => student.username === this.state.user).stableIdV1,
						appControlId: appControl.id,
						action: this.state.type === "blocked" ? "block" : "allow",
					},
				},
				{ onApiError: this.handleOnApiError, callback: this.handleAppControlsValidationResponse }
			);
		}
	};

	modify = (event = {}) => {
		if (!this.validate()) return;
		this.setState({ saving: true });

		if (_.isEmpty(this.props.nativeAppControl)) {
			if (this.state.originalType === "allowed") {
				useClassroomPolicyStore.getState().modifyAllowedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.props.policy.enabled,
						user: this.state.wholeClass ? undefined : this.state.user,
						type: this.state.type,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			} else {
				useClassroomPolicyStore.getState().modifyBlockedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.props.policy.enabled,
						user: this.state.wholeClass ? undefined : this.state.user,
						type: this.state.type,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			}
		} else {
			// native app control
			useClassroomPolicyStore.getState().modifyAppControl(
				{
					classroomStableIdV1: useSelectedClassroomStore.getState().stableIdV1,
					classroomId: useSelectedClassroomStore.getState().id,
					classroomSourceName: useSelectedClassroomStore.getState().sourceId,
					data: {
						id: this.props.nativeAppControl.id,
						name: this.props.nativeAppControl.name,
						isClassroomRule: this.state.wholeClass,
						studentStableIdV1: this.state.wholeClass
							? null
							: useSelectedClassroomStore.getState().students?.find((student) => student.username === this.state.user).stableIdV1,
						appControlId: this.state.signature,
						action: this.state.type === "blocked" ? "block" : "allow",
					},
				},
				{ callback: this.handleAppControlsValidationResponse }
			);
		}
	};

	delete = (event = {}) => {
		this.setState({ saving: true });
		if (_.isEmpty(this.props.nativeAppControl)) {
			if (this.state.originalType === "blocked") {
				useClassroomPolicyStore.getState().removeBlockedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.props.policy.enabled,
						user: this.state.originalUser,
					},
					{ onApiError: this.handleOnApiError, callback: this.handleValidationResponse }
				);
			} else {
				useClassroomPolicyStore.getState().removeAllowedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.props.policy.enabled,
						user: this.state.originalUser,
					},
					{ onApiError: this.handleOnApiError, callback: this.handleValidationResponse }
				);
			}
		} else {
			// native app control
			useClassroomPolicyStore.getState().removeAppControl(
				{
					id: this.props.nativeAppControl.id,
					classroomStableIdV1: useSelectedClassroomStore.getState().stableIdV1,
					classroomId: useSelectedClassroomStore.getState().id,
					classroomSourceName: useSelectedClassroomStore.getState().sourceId,
				},
				{
					callback: this.handleValidationResponse,
				}
			);
		}
	};

	resolve = () => {
		this.setState({
			duplicateOrConflictRule: undefined,
			resolving: true,
			validationErrorCode: undefined,
			validatedPolicy: undefined,
			id: this.state.duplicateOrConflictRule.id,
			type: this.state.duplicateOrConflictRule.type,
			originalType: this.state.duplicateOrConflictRule.type,
			wholeClass: this.state.duplicateOrConflictRule.user ? false : true,
			user: this.state.duplicateOrConflictRule.user,
			originalUser: this.state.duplicateOrConflictRule.user,
			signature: this.state.duplicateOrConflictRule.tag,
			website: this.state.duplicateOrConflictRule.website,
		});
	};

	resolveWithValidationsOff = () => {
		if (this.props.editing) {
			this.modify({ skipValidations: true });
		} else {
			this.submit({ skipValidations: true });
		}
	};

	close = () => {
		this.setState({
			id: undefined,
			signature: undefined,
			signatureType: undefined,
			website: undefined,
			user: undefined,
			originalUser: undefined,
			editedlUser: undefined,
			type: "blocked",
			originalType: "blocked",
			signatureValueInvalid: false,
			userValueInvalid: false,
			wholeClass: true,
			saving: false,
			duplicateOrConflictRule: undefined,
			resolving: false,
			validationErrorCode: undefined,
			validatedPolicy: undefined,
			isBrowser: false,
			appControlValidationError: false,
			networkError: false,
		});

		this.props.onClose();
	};

	renderButtons = () => {
		if (this.state.saving) {
			return <PrimaryButton className="mui-flat-button" label={<i className="fa fa-refresh fa-spin fa-fw" />} />;
		}

		if (this.state.networkError) {
			return <GreyedButton className="mui-flat-button" label="Try again" onClick={this.close} />;
		}

		if (this.state.duplicateOrConflictRule) {
			if (
				this.props.editing &&
				(this.state.validationErrorCode.includes("SM") ||
					["CW001", "CW002", "CW003", "CW004", "CW005"].includes(this.state.validationErrorCode))
			) {
				return (
					<div>
						<DangerButton className="mui-flat-button" label="Delete rule" onClick={this.delete} />
					</div>
				);
			} else if (this.state.validationErrorCode.includes("SM") || ["CW006"].includes(this.state.validationErrorCode)) {
				return (
					<div>
						<GreyedButton className="mui-flat-button" label="Add rule" />
					</div>
				);
			} else if (["CW007"].includes(this.state.validationErrorCode)) {
				return (
					<div>
						<PrimaryButton className="mui-flat-button" label="yes, Proceed" onClick={this.resolveWithValidationsOff} />
					</div>
				);
			} else {
				return (
					<div>
						<PrimaryButton className="mui-flat-button" label="yes, Edit" onClick={this.resolve} />
					</div>
				);
			}
		}

		if (this.props.editing || this.state.resolving) {
			return (
				<div>
					<DangerButton className="mui-flat-button" label="Delete rule" onClick={this.delete} />
					<PrimaryButton className="mui-flat-button" label="Update" onClick={this.modify} />
				</div>
			);
		}

		return (
			<div>
				<PrimaryButton className="mui-flat-button" label="Add rule" onClick={this.submit} />
			</div>
		);
	};

	openZenDeskHowClasswizeRulesWork = (event) => {
		window.open("https://help.classwize.net/hc/en-gb/articles/5590349042204-How-Classwize-Rules-Work", "_blank");
	};

	getDisplayName = (username) => {
		if (!username) {
			return "the whole class";
		}
		const user = this.props.users.filter((user) => user.username === username)[0];
		return userDisplayText(user);
	};

	renderServerValidationWarnings = (policy) => {
		if (!this.state.duplicateOrConflictRule) {
			return;
		}

		if (this.state.validationErrorCode.includes("CW")) {
			if (this.props.editing) {
				if (["CW002"].includes(this.state.validationErrorCode)) {
					const action = this.state.duplicateOrConflictRule.type === "blocked" ? "blocked from" : "allowed to access";
					const actionShort = this.state.duplicateOrConflictRule.type === "blocked" ? "block" : "allow";
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								The whole class is already {action} <b>{this.renderApp(this.state.duplicateOrConflictRule)}</b>. To {actionShort}{" "}
								{this.getDisplayName(this.state.originalUser)} as well, delete this rule.
							</li>
						</ul>
					);
				} else if (["CW001", "CW004"].includes(this.state.validationErrorCode)) {
					const action = this.state.duplicateOrConflictRule.type === "blocked" ? "from" : "to access";
					const actionShort = this.state.duplicateOrConflictRule.type === "blocked" ? "block" : "allow";
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								A rule to {actionShort} the whole class {action}
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> already exists, would you like to delete this rule for{" "}
								{this.getDisplayName(this.state.originalUser)}.
							</li>
						</ul>
					);
				} else if (["CW003", "CW005"].includes(this.state.validationErrorCode)) {
					const action = this.state.duplicateOrConflictRule.type === "blocked" ? "blocked from" : "allowed to access";
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								{this.getDisplayName(this.state.editedlUser)} is already {action}{" "}
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b>. Would you like to delete this rule for{" "}
								{this.getDisplayName(this.state.originalUser)}.
							</li>
						</ul>
					);
				} else if (["CW006"].includes(this.state.validationErrorCode)) {
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> uses content from {this.renderApp(this.state.validatedPolicy)}.{" "}
								{this.renderApp(this.state.validatedPolicy)} can't be blocked.
							</li>
						</ul>
					);
				} else if (["CW007"].includes(this.state.validationErrorCode)) {
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> is currently blocked. Editing this rule will allow{" "}
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b>, are you sure you would like to proceed?
							</li>
						</ul>
					);
				}
			} else {
				if (["CW006"].includes(this.state.validationErrorCode)) {
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> uses content from {this.renderApp(this.state.validatedPolicy)}.{" "}
								{this.renderApp(this.state.validatedPolicy)} can't be blocked.
							</li>
						</ul>
					);
				} else if (["CW007"].includes(this.state.validationErrorCode)) {
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> is currently blocked. Creating this rule will allow{" "}
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b>, are you sure you would like to proceed?
							</li>
						</ul>
					);
				} else {
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							</li>
							<li>
								A rule for <b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> already exists, would you like to edit it now?
							</li>
						</ul>
					);
				}
			}
		} else if (this.state.validationErrorCode.includes("SM")) {
			let lockedRuleType;
			if (["SM001", "SM002", "SM006"].includes(this.state.validationErrorCode)) {
				lockedRuleType = "allowed";
			} else {
				// "SM003", "SM004"
				lockedRuleType = "blocked";
			}

			if (["SM001", "SM002", "SM003", "SM004"].includes(this.state.validationErrorCode)) {
				return (
					<div>
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-lock CreatePolicy_WarningIcon" />
							</li>
							<li>
								<div className="CreatePolicy_WarningText">
									<b>Locked rule</b>
								</div>
								Your school has {lockedRuleType} <b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> with a locked rule. You can't
								create this rule.
							</li>
						</ul>
						<ul className="CreatePolicy_Info">
							<li>
								<i className="fa fa-info-circle CreatePolicy_InfoIcon" />
							</li>
							<li>
								<div className="CreatePolicy_InfoText">
									<b>What can I do?</b>
								</div>
								Contact your local IT support team to learn why <b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> is locked and if
								it can be unlocked.
								<div>
									<button onClick={this.openZenDeskHowClasswizeRulesWork}>
										Learn more about Locked rules
										<img className="CreatePolicy_newWindowIcon" src={iconNewWindow} alt="" />
									</button>
								</div>
							</li>
						</ul>
					</div>
				);
			} else {
				return (
					<div>
						<ul className="CreatePolicy_Warning">
							<li>
								<i className="fa fa-lock CreatePolicy_WarningIcon" />
							</li>
							<li>
								<div className="CreatePolicy_WarningText">
									<b>Your school has a locked rule for {this.renderApp(this.state.duplicateOrConflictRule)}</b>
								</div>
								<b>{this.renderApp(this.state.duplicateOrConflictRule)}</b> uses content from {this.renderApp(this.state.validatedPolicy)}.{" "}
								{this.renderApp(this.state.validatedPolicy)} can't be blocked.
							</li>
						</ul>
						<ul className="CreatePolicy_Info">
							<li>
								<i className="fa fa-info-circle CreatePolicy_InfoIcon" />
							</li>
							<li>
								<div className="CreatePolicy_InfoText">
									<b>What can I do?</b>
								</div>
								Contact your local IT support team to learn more.
								<button onClick={this.openZenDeskHowClasswizeRulesWork}>
									Learn more about Locked rules
									<img className="CreatePolicy_newWindowIcon" src={iconNewWindow} alt="" />
								</button>
							</li>
						</ul>
					</div>
				);
			}
		}
	};

	renderAppControlsWarnings = () => {
		if (this.state.appControlValidationError) {
			const appControl = this.props.nativeAppControlsDb.find(this.state.signature);
			setTimeout(() => {
				this.formRef.current?.scrollIntoView({ block: "end" });
			}, 16);
			const mutationAction = this.props.editing ? "update" : "create";
			return (
				<ul className="CreatePolicy_Warning">
					<li>
						<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
					</li>
					<li>
						Cannot {mutationAction} this rule as it conflicts with an existing rule for {appControl.name}
					</li>
				</ul>
			);
		}
	};

	renderApp = (policy) => {
		if (policy.website) {
			return policy.website;
		}

		if (policy.tag) {
			return this.props.signatureDb.lookup(policy.tag);
		}

		return "all apps";
	};

	renderWebsiteIcon = (policy) => {
		if (policy.tag) {
			const faviconUrl = this.props.signatureDb.favicon(policy.tag);

			if (faviconUrl && faviconUrl !== "") {
				return (
					<div>
						<SafeImage styleClassName="CreatePolicy_ruleFavicon" src={faviconUrl} />
					</div>
				);
			}
		} else if (policy.website) {
			return <SafeImage styleClassName="CreatePolicy_ruleFavicon" src="/static/images/Icon_URL.svg" />;
		}

		return <SafeImage styleClassName="CreatePolicy_ruleFavicon" src="/static/images/Icon_Folder.svg" />;
	};

	renderWebsite = (policy) => {
		if (this.state.resolving || this.props.editing) {
			if (_.isEmpty(this.props.nativeAppControl)) {
				return (
					<div>
						{this.renderWebsiteIcon(policy)}
						{this.renderApp(policy)}
					</div>
				);
			} else {
				// native app control
				return (
					<div>
						{/* icon */}
						{!_.isEmpty(this.props.nativeAppControl.favicon) ? (
							<SafeImage styleClassName="CreatePolicy_ruleFavicon" src={this.props.nativeAppControl.favicon} />
						) : (
							<SafeImage styleClassName="CreatePolicy_ruleFavicon" src="/static/images/Icon_Folder.svg" />
						)}
						{/* name */}
						{this.props.nativeAppControl.name}
					</div>
				);
			}
		} else {
			return (
				<div>
					<label className="block-label--bold">This rule controls</label>
					<NuSingleSelect
						tabIndex={0}
						allItems={this.prepareSelectorItems()}
						selectedItem={this.getSelected()}
						searchPlaceholder="Search"
						onChange={this.changeSignature}
						invalid={this.state.signatureValueInvalid}
					/>
					{this.state.signatureValueInvalid && <div className="error">{this.state.signatureError}</div>}
					{this.addWarningsToBlockBrowsers()}
				</div>
			);
		}
	};

	addWarningsToBlockBrowsers = () => {
		return (
			this.state.isBrowser && (
				<ul className="CreatePolicy_Warning">
					<li>
						<div className="CreatePolicy_Warning_Title">
							<i className="fa fa-exclamation-triangle CreatePolicy_WarningIcon" />
							Take care when blocking browsers
						</div>
						<div className="CreatePolicy_Warning_Body">
							Classwize "Focus" and "Open Site" use the default browser. If you accidentally block the default browser, then these features
							will not work.
						</div>
					</li>
				</ul>
			)
		);
	};

	getSelected = () => {
		if (this.state.website) {
			return !this.state.modifiedWebsite ? this.state.website : "website";
		}
		return this.state.signature;
	};

	render() {
		const modalTitle = (this.props.editing || this.state.resolving) !== true ? "Add new rule" : "Edit class rule";

		const policy = {
			id: this.state.id,
			tag: this.state.signature,
			website: this.state.website,
			permanent: true,
			user: this.state.user,
		};

		return (
			<div>
				<StandardModal title={modalTitle} open={this.props.open} actions={this.renderButtons()} close={this.close}>
					<form onSubmit={this.submit} ref={this.formRef}>
						<div>
							<div style={{ lineHeight: "1.5" }}>
								Control what students access during your class.
								{this.featureFlags.isNativeAppControlsEnabled() ? (
									<>
										<br />
										<a className="link" href="https://help.classwize.net/hc/en-gb/articles/8675151325212" target="_blank" rel="noreferrer">
											View the list of applications
										</a>{" "}
										that you can block on Windows devices.
									</>
								) : null}
							</div>
							<div className="Modal_InfoPanel_No_Title">
								<div className="Heading">
									<svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path
											d="M5 0.25C2.22656 0.25 0 2.49609 0 5.25C0 8.02344 2.22656 10.25 5 10.25C7.75391 10.25 10 8.02344 10 5.25C10 2.49609 7.75391 0.25 5 0.25ZM5 2.75C5.33203 2.75 5.625 3.04297 5.625 3.375C5.625 3.72656 5.33203 4 5 4C4.64844 4 4.375 3.72656 4.375 3.375C4.375 3.04297 4.64844 2.75 5 2.75ZM5.78125 7.75H4.21875C3.94531 7.75 3.75 7.55469 3.75 7.28125C3.75 7.02734 3.94531 6.8125 4.21875 6.8125H4.53125V5.5625H4.375C4.10156 5.5625 3.90625 5.36719 3.90625 5.09375C3.90625 4.83984 4.10156 4.625 4.375 4.625H5C5.25391 4.625 5.46875 4.83984 5.46875 5.09375V6.8125H5.78125C6.03516 6.8125 6.25 7.02734 6.25 7.28125C6.25 7.55469 6.03516 7.75 5.78125 7.75Z"
											fill="#17B0A8"
										/>
									</svg>
								</div>
								Some of your rules may not work if your IT administrator has created locked rules for them. <br />
								Contact your IT administrator to know why rules have been locked.
								<a href="https://help.classwize.net/hc/en-gb/articles/5573453442972#locked-0-2" target="_blank" rel="noopener noreferrer">
									&nbsp;Learn more about locked rules
								</a>
							</div>
							<br />
							{this.renderWebsite(policy)}
							<br />
							<div>
								<label htmlFor="create_rule_status_toggle" className="block-label--bold">
									During class it should be
								</label>
								{this.state.classwizeConfig.data.create_blocking_rules_only ? (
									<RuleStatusPill type="blocked" />
								) : (
									<RuleStatusToggle id="create_rule_status_toggle" onChange={this.changeType} type={this.state.type} />
								)}
							</div>
							<br />
							<div className="CreatePolicy_checkboxRow">
								<div className="CreatePolicy_checkboxRowLabel">
									<label className="text-bold">This rule applies to</label>
								</div>
								<div className="CreatePolicy_checkboxRowCheckbox">
									<Checkbox
										checked={this.state.wholeClass}
										onChecked={(isChecked) => this.setState({ wholeClass: isChecked })}
										label="Whole class"
									/>
								</div>
							</div>
							<div>
								<DisableKeyboardNavigation enabled={this.state.wholeClass} className="disabled-wrapper">
									<NuSingleSelect
										allItems={this.prepareAvailableUsers()}
										selectedItem={this.state.user}
										searchPlaceholder="Search and select for a student (optional)"
										onChange={this.changeUser}
										invalid={!this.state.wholeClass && this.state.userValueInvalid}
									/>
									{!this.state.wholeClass && this.state.userValueInvalid && <div className="error">{this.state.userError}</div>}
								</DisableKeyboardNavigation>
							</div>
							{this.state.signatureType === APP_CONTROL ? this.renderAppControlsWarnings() : this.renderServerValidationWarnings(policy)}
						</div>
					</form>
				</StandardModal>
			</div>
		);
	}
}

CreatePolicy.propTypes = {
	signatureDb: PropTypes.object,
	nativeAppControlsDb: PropTypes.object,
	policy: PropTypes.object,
	onClose: PropTypes.func,
	open: PropTypes.bool,
	editing: PropTypes.bool,
	validationResponseData: PropTypes.object,
	users: PropTypes.array,
};
